
import * as XLSX from 'xlsx';
import { supabase } from "@/integrations/supabase/client";
import { LeaderboardItem, SupabaseLeaderboardItem } from "./types";

export const saveLeaderboardData = async (data: LeaderboardItem[]) => {
  try {
    const { error: deleteError } = await supabase.from("leaderboard").delete().neq("id", 0);
    if (deleteError) {
      console.error("Error deleting existing leaderboard data:", deleteError);
      return;
    }
    const supabaseData = data.map(item => ({
      id: parseInt(item.id),
      name: item.name,
      region: item.region,
      product_line: item.productLine,
      industry: item.industry,
      target: item.target,
      achievement: item.achievement,
      cogs: item.cogs,
      forecast: item.forecast,
      y_1_forecast: item.y1Forecast,
      y_2_forecast: item.y2Forecast,
      y_1_actuals: item.y1Actuals,
      y_2_actuals: item.y2Actuals,
      account_name: item.accountName,
      country: item.country,
      revenue_gap: item.revenueGap,
      prospecting_stage_value: item.prospectingStageValue,
      qualification_stage_value: item.qualificationStageValue,
      proposal_stage_value: item.proposalStageValue,
      negotiation_stage_value: item.negotiationStageValue,
      late_stage_total_value: item.lateStageTotal,
      late_stage_to_gap_ratio: item.lateStageToGapRatio,
      historical_win_ratio: item.historicalWinRatio
    }));
    
    console.log("Saving data to Supabase:", supabaseData);
    const { error: insertError } = await supabase.from("leaderboard").insert(supabaseData);
    if (insertError) {
      console.error("Error inserting leaderboard data:", insertError);
    }
  } catch (err) {
    console.error("Unexpected error saving leaderboard data:", err);
  }
};

export const processImportedData = async (jsonData: any[], setErrorMessage: (message: string) => void, setIsErrorDialogOpen: (isOpen: boolean) => void, setOriginalData: (data: LeaderboardItem[]) => void) => {
  try {
    if (!jsonData || jsonData.length === 0) {
      setErrorMessage("No data found in the Excel file");
      setIsErrorDialogOpen(true);
      return;
    }
    
    const firstRow = jsonData[0];
    console.log("First row data:", firstRow);
    
    const idKeys = ['id', 'ID', 'Id', 'identifier', 'Identifier', 'record_id', 'Record ID', 'RecordID', 'recordID', 'emp_id', 'employee_id', 'EmployeeID'];
    const nameKeys = ['name', 'Name', 'employee_name', 'Employee Name', 'employeeName', 'EmployeeName', 'sales_rep', 'Sales Rep', 'salesRep', 'SalesRep', 'rep_name', 'RepName', 'full_name', 'FullName'];
    const regionKeys = ['region', 'Region', 'sales_region', 'Sales Region', 'salesRegion', 'SalesRegion', 'territory', 'Territory', 'area', 'Area', 'geo', 'Geo', 'geography', 'Geography'];
    const productLineKeys = ['productLine', 'ProductLine', 'product_line', 'Product Line', 'product', 'Product', 'solution', 'Solution', 'offering', 'Offering', 'service_line', 'ServiceLine', 'service', 'Service'];
    const industryKeys = ['industry', 'Industry', 'sector', 'Sector', 'market', 'Market', 'vertical', 'Vertical', 'segment', 'Segment', 'business_segment', 'BusinessSegment', 'business_type', 'BusinessType'];
    const targetKeys = ['target', 'Target', 'goal', 'Goal', 'quota', 'Quota', 'sales_target', 'Sales Target', 'salesTarget', 'SalesTarget', 'plan', 'Plan', 'planned', 'Planned', 'budget', 'Budget'];
    const achievementKeys = ['achievement', 'Achievement', 'actual', 'Actual', 'sales', 'Sales', 'revenue', 'Revenue', 'performance', 'Performance', 'attainment', 'Attainment', 'result', 'Result', 'realized', 'Realized'];
    const cogsKeys = ['cogs','COGS', 'Cogs', 'Cost Of Goods Sold', 'cost_of_goods_sold'];
    const forecastKeys = ['forecast', 'Forecast', 'projection', 'Projection', 'expected', 'Expected', 'estimated', 'Estimated', 'outlook', 'Outlook', 'prediction', 'Prediction', 'future', 'Future', 'upcoming', 'Upcoming'];
    const y1ForecastKeys = ['Y-1 Forecast', 'y-1 forecast', 'y1forecast', 'y-1forecast', 'y1 forecast', 'y_1_forecast', 'prior_year_forecast', 'prioryearforecast', 'PriorYearForecast', 'LastYearForecast', 'lastyearforecast', 'previous_year_forecast', 'prev_year_forecast', 'forecast_prior_year', 'forecast_prev_year', 'last_year_projection', 'previous_projection'];
    const y2ForecastKeys = ['Y-2 Forecast', 'y-2 forecast', 'y2forecast', 'y-2forecast', 'y2 forecast', 'y_2_forecast', 'two_years_ago_forecast', 'twoyearsagoforecast', 'TwoYearsAgoForecast', 'forecast_2_years_ago', 'projection_2_years_ago', 'two_year_prior_forecast', 'forecast_two_years_prior'];
    const y1ActualsKeys = ['Y-1 Actuals', 'y-1 actuals', 'y1actuals', 'y-1actuals', 'y1 actuals', 'y_1_actuals', 'prior_year_actuals', 'prioryearactuals', 'PriorYearActuals', 'LastYearActuals', 'lastyearactuals', 'previous_year_actual', 'prev_year_actual', 'last_year_results', 'prior_year_results', 'previous_achievement'];
    const y2ActualsKeys = ['Y-2 Actuals', 'y-2 actuals', 'y2actuals', 'y-2actuals', 'y2 actuals', 'y_2_actuals', 'two_years_ago_actuals', 'twoyearsagoactuals', 'TwoYearsAgoActuals', 'actuals_2_years_ago', 'results_2_years_ago', 'two_year_prior_results', 'two_years_prior_achievement'];
    const accountNameKeys = ['account_name', 'accountName', 'AccountName', 'Account Name', 'Account', 'account', 'Customer', 'customer', 'CustomerName', 'Client', 'client', 'ClientName'];
    const countryKeys = ['country', 'Country', 'Nation', 'nation', 'Location', 'location', 'CountryName', 'country_name'];
    const revenueGapKeys = ['revenue_gap', 'revenueGap', 'Revenue_Gap', 'RevenueGap', 'Gap', 'gap', 'Revenue Gap', 'Target Gap', 'TargetGap', 'target_gap'];
    const prospectingStageValueKeys = ['prospecting_stage_value', 'prospectingStageValue', 'ProspectingStage_Value', 'Prospecting_Value', 'Prospecting Stage', 'Prospecting Value', 'ProspectingValue'];
    const qualificationStageValueKeys = ['qualification_stage_value', 'qualificationStageValue', 'QualificationStage_Value', 'Qualification_Value', 'Qualification Stage', 'Qualification Value', 'QualificationValue'];
    const proposalStageValueKeys = ['proposal_stage_value', 'proposalStageValue', 'ProposalStage_Value', 'Proposal_Value', 'Proposal Stage', 'Proposal Value', 'ProposalValue'];
    const negotiationStageValueKeys = ['negotiation_stage_value', 'negotiationStageValue', 'NegotiationStage_Value', 'Negotiation_Value', 'Negotiation Stage', 'Negotiation Value', 'NegotiationValue'];
    const lateStageTotalValueKeys = ['late_stage_total_value', 'lateStageTotal', 'LateStage_Total_Value', 'LateStage_Value', 'Late Stage Total', 'Late Stage Value', 'LateStageTotalValue'];
    const lateStageToGapRatioKeys = ['late_stage_to_gap_ratio', 'lateStageToGapRatio', 'LateStage_To_Gap_Ratio', 'Gap Ratio', 'GapRatio', 'gap_ratio', 'Late Stage Ratio'];
    const historicalWinRatioKeys = ['historical_win_ratio', 'historicalWinRatio', 'Historical_Win_Ratio', 'Win Ratio', 'WinRatio', 'win_ratio', 'Historical Win Rate'];
    
    const requiredFields = [
      { name: 'name', keys: nameKeys }, 
      { name: 'region', keys: regionKeys }, 
      { name: 'productLine', keys: productLineKeys }, 
      { name: 'industry', keys: industryKeys }, 
      { name: 'target', keys: targetKeys }, 
      { name: 'achievement', keys: achievementKeys }
    ];
    
    const missingFields = requiredFields.filter(field => {
      return !field.keys.some(key => key in firstRow);
    });
    
    if (missingFields.length > 0) {
      setErrorMessage(`Missing required columns: ${missingFields.map(f => f.name).join(', ')}`);
      setIsErrorDialogOpen(true);
      return;
    }
    
    const transformedData: LeaderboardItem[] = [];
    
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      let id = String(i + 1);
      for (const key of idKeys) {
        if (row[key] !== undefined) {
          id = String(row[key]);
          break;
        }
      }
      let name = "Unknown";
      for (const key of nameKeys) {
        if (row[key] !== undefined) {
          name = row[key];
          break;
        }
      }
      let region = "Unknown Region";
      for (const key of regionKeys) {
        if (row[key] !== undefined) {
          region = row[key];
          break;
        }
      }
      let productLine = "Unknown Product";
      for (const key of productLineKeys) {
        if (row[key] !== undefined) {
          productLine = row[key];
          break;
        }
      }
      let industry = "Unknown Industry";
      for (const key of industryKeys) {
        if (row[key] !== undefined) {
          industry = row[key];
          break;
        }
      }
      let target = 0;
      for (const key of targetKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            target = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedTarget = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedTarget)) {
              target = parsedTarget;
              break;
            }
          }
        }
      }
      let achievement = 0;
      for (const key of achievementKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            achievement = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedAchievement = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedAchievement)) {
              achievement = parsedAchievement;
              break;
            }
          }
        }
      }
      let cogs = 0;
      for (const key of cogsKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            cogs = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedCogs = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedCogs)) {
              cogs = parsedCogs;
              break;
            }
          }
        }
      }
      
      // Process other fields
      let forecast = null;
      for (const key of forecastKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            forecast = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedForecast = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedForecast)) {
              forecast = parsedForecast;
              break;
            }
          }
        }
      }
      
      // Process all remaining fields
      let y1Forecast = null;
      for (const key of y1ForecastKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            y1Forecast = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedForecast = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedForecast)) {
              y1Forecast = parsedForecast;
              break;
            }
          }
        }
      }
      
      let y2Forecast = null;
      for (const key of y2ForecastKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            y2Forecast = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedForecast = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedForecast)) {
              y2Forecast = parsedForecast;
              break;
            }
          }
        }
      }
      
      let y1Actuals = null;
      for (const key of y1ActualsKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            y1Actuals = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedActuals = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedActuals)) {
              y1Actuals = parsedActuals;
              break;
            }
          }
        }
      }
      
      let y2Actuals = null;
      for (const key of y2ActualsKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            y2Actuals = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedActuals = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedActuals)) {
              y2Actuals = parsedActuals;
              break;
            }
          }
        }
      }

      let accountName = null;
      for (const key of accountNameKeys) {
        if (row[key] !== undefined) {
          accountName = row[key];
          break;
        }
      }
      
      let country = null;
      for (const key of countryKeys) {
        if (row[key] !== undefined) {
          country = row[key];
          break;
        }
      }
      
      let revenueGap = null;
      for (const key of revenueGapKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            revenueGap = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedValue = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedValue)) {
              revenueGap = parsedValue;
              break;
            }
          }
        }
      }
      
      let prospectingStageValue = null;
      for (const key of prospectingStageValueKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            prospectingStageValue = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedValue = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedValue)) {
              prospectingStageValue = parsedValue;
              break;
            }
          }
        }
      }
      
      let qualificationStageValue = null;
      for (const key of qualificationStageValueKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            qualificationStageValue = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedValue = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedValue)) {
              qualificationStageValue = parsedValue;
              break;
            }
          }
        }
      }
      
      let proposalStageValue = null;
      for (const key of proposalStageValueKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            proposalStageValue = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedValue = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedValue)) {
              proposalStageValue = parsedValue;
              break;
            }
          }
        }
      }
      
      let negotiationStageValue = null;
      for (const key of negotiationStageValueKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            negotiationStageValue = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedValue = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedValue)) {
              negotiationStageValue = parsedValue;
              break;
            }
          }
        }
      }
      
      let lateStageTotal = null;
      for (const key of lateStageTotalValueKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            lateStageTotal = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedValue = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedValue)) {
              lateStageTotal = parsedValue;
              break;
            }
          }
        }
      }
      
      let lateStageToGapRatio = null;
      for (const key of lateStageToGapRatioKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            lateStageToGapRatio = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedValue = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedValue)) {
              lateStageToGapRatio = parsedValue;
              break;
            }
          }
        }
      }
      
      let historicalWinRatio = null;
      for (const key of historicalWinRatioKeys) {
        if (row[key] !== undefined) {
          if (typeof row[key] === 'number') {
            historicalWinRatio = row[key];
            break;
          } else if (typeof row[key] === 'string') {
            const parsedValue = parseFloat(row[key].replace(/,/g, ''));
            if (!isNaN(parsedValue)) {
              historicalWinRatio = parsedValue;
              break;
            }
          }
        }
      }
      
      const percentage = target > 0 ? achievement / target * 100 : 0;
      transformedData.push({
        id,
        name,
        region,
        productLine,
        industry,
        target,
        achievement,
        cogs,
        percentage: parseFloat(percentage.toFixed(2)),
        forecast,
        y1Forecast,
        y2Forecast,
        y1Actuals,
        y2Actuals,
        accountName,
        country,
        revenueGap,
        prospectingStageValue,
        qualificationStageValue,
        proposalStageValue,
        negotiationStageValue,
        lateStageTotal,
        lateStageToGapRatio,
        historicalWinRatio
      });
    }

    transformedData.sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
    setOriginalData(transformedData);
    saveLeaderboardData(transformedData);
  } catch (err) {
    console.error("Error processing imported data:", err);
    setErrorMessage("Error processing the imported data. Please check the file format.");
    setIsErrorDialogOpen(true);
  }
};

export const processFile = (
  file: File, 
  fileInputRef: React.RefObject<HTMLInputElement>,
  setErrorMessage: (message: string) => void,
  setIsErrorDialogOpen: (isOpen: boolean) => void,
  setIsImportDialogOpen: (isOpen: boolean) => void,
  processImportedDataFn: (jsonData: any[]) => void
) => {
  if (!file) return;
  
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  
  if (fileExtension !== 'xls' && fileExtension !== 'xlsx') {
    setErrorMessage("Please select a valid Excel file (.xls or .xlsx)");
    setIsErrorDialogOpen(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    return;
  }
  
  const reader = new FileReader();
  
  reader.onload = evt => {
    try {
      const binaryStr = evt.target?.result;
      const workbook = XLSX.read(binaryStr, {
        type: 'binary'
      });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      console.log("Imported Excel data:", jsonData);
      processImportedDataFn(jsonData);
      setIsImportDialogOpen(false);
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      setErrorMessage("Error parsing Excel file. Please check the file format.");
      setIsErrorDialogOpen(true);
    }
  };
  
  reader.onerror = () => {
    setErrorMessage("Error reading file");
    setIsErrorDialogOpen(true);
  };
  
  reader.readAsBinaryString(file);
  
  if (fileInputRef.current) {
    fileInputRef.current.value = '';
  }
};
